// useAppExit — pede pra fechar o app e CONFIRMA o desfecho.
//
// `exitApp()` (platform-exit.ts) tenta a capability de saída da plataforma, mas
// em nenhum alvo dá pra saber sincronamente se fechou. Se o app continuar vivo,
// a tela ficaria com um botão "Sair" que não sai — o usuário não tem rota de
// escape e desinstala o app (foi o que aconteceu na LG do dono).
//
// Aqui a saída é verificada: se depois de `GRACE_MS` o React ainda está vivo, o
// exit FALHOU e a UI mostra a alternativa honesta ("use o botão Home do
// controle"). Se o app fechou, o timer morre com a página e nada aparece.
//
// FIX 2026-07 (aviso FALSO no webOS): "a página morre junto" NÃO vale pra toda
// plataforma. No webOS a saída canônica é `platformBack()`, que manda o app pro
// BACKGROUND — o processo e a página SOBREVIVEM. O timer de graça seguia correndo
// escondido, disparava, e ao RELANÇAR pelo launcher o usuário encontrava o aviso
// "sua TV não deixa o app fechar" — apesar de a saída ter funcionado. Agora o
// veredito é cancelado quando a página vai pro background/é descarregada depois do
// pedido de saída (ver `armBackgroundCancel` abaixo).

import { useCallback, useEffect, useRef, useState } from 'react'

import { exitApp, type ExitMethod } from '../platform-exit'

/** Janela de graça pra plataforma fechar o app antes de declararmos falha.
 *  Generoso pra TV 2017 (WebView lento) sem deixar o usuário no limbo. */
const GRACE_MS = 1500

/**
 * Métodos em que "o app sumiu da tela" É o sucesso da saída — pra eles, um
 * `visibilitychange → hidden` depois do pedido PROVA que o exit funcionou.
 *
 *  - `webos`: platformBack() devolve o controle ao launcher SEM matar a página.
 *    É o caso do bug: sem este cancelamento, o aviso falso ficava armado.
 *  - `tizen`: app.exit() encerra o processo (a página morre e o timer vai junto),
 *    mas se algum firmware apenas esconder o app o desfecho é o mesmo — sucesso.
 *
 * `window-close` fica DE FORA de propósito: ali `hidden` significa "trocou de
 * aba/app", não "fechou". Cancelar o timer nesse caso trocaria o aviso falso por
 * um bug pior — o botão "Sair" ficaria SEM NENHUM retorno no browser/Android TV,
 * que é justamente onde `window.close()` é no-op e o aviso é a única saída honesta.
 */
const BACKGROUND_MEANS_EXITED: readonly ExitMethod[] = ['webos', 'tizen']

export interface UseAppExitResult {
  /** Dispara a saída. */
  requestExit: () => void
  /** true quando a plataforma NÃO fechou o app — a UI deve mostrar o fallback. */
  exitFailed: boolean
  /** Fecha o aviso de falha (o usuário continua no app). */
  dismissExitFailed: () => void
}

export function useAppExit(): UseAppExitResult {
  const [exitFailed, setExitFailed] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  /** Método do pedido de saída em voo (null = nenhum pedido pendente). */
  const pendingMethodRef = useRef<ExitMethod | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current) clearTimeout(timerRef.current)
    timerRef.current = null
  }, [])

  const requestExit = useCallback(() => {
    const method = exitApp()
    if (method === 'unsupported') {
      // Nem tentar esperar: não há capability de saída neste alvo.
      pendingMethodRef.current = null
      setExitFailed(true)
      return
    }
    clearTimer()
    pendingMethodRef.current = method
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      setExitFailed(true)
    }, GRACE_MS)
  }, [clearTimer])

  const dismissExitFailed = useCallback(() => {
    clearTimer()
    pendingMethodRef.current = null
    setExitFailed(false)
  }, [clearTimer])

  useEffect(() => {
    /** A saída funcionou: cancela o timer E derruba um veredito já emitido.
     *  O 2º caso importa na TV 2017 real (WebView lento): se o background demorar
     *  mais que GRACE_MS, o aviso JÁ subiu — e sem este reset ele estaria na tela
     *  esperando o usuário quando ele relançasse o app pelo launcher. */
    const exitConfirmed = () => {
      if (pendingMethodRef.current == null) return
      clearTimer()
      pendingMethodRef.current = null
      setExitFailed(false)
    }

    const onVisibilityChange = () => {
      if (document.visibilityState !== 'hidden') return
      const method = pendingMethodRef.current
      if (method != null && BACKGROUND_MEANS_EXITED.includes(method)) exitConfirmed()
    }

    // pagehide vale pra QUALQUER método: a página está sendo descarregada (ou
    // congelada em bfcache, onde os timers RETOMAM na volta e disparariam o aviso
    // fora de hora). Se havia pedido de saída em voo, o desfecho foi sucesso.
    const onPageHide = () => exitConfirmed()

    document.addEventListener('visibilitychange', onVisibilityChange)
    window.addEventListener('pagehide', onPageHide)
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange)
      window.removeEventListener('pagehide', onPageHide)
      clearTimer()
    }
  }, [clearTimer])

  return { requestExit, exitFailed, dismissExitFailed }
}
